// Seiten, die es schon gibt, deren Inhalt aber noch kommt (CLAUDE.md,
// Grundsatz 8: „Was später kommt, ist als Platzhalter sichtbar“) – ehrlich,
// mit einem Satz, was dort hinkommt, und wann. Dazu die Kisten, die noch
// nicht gebaut sind (Mockup /crm/kiste/:id: „Läuft bis dahin wie heute“),
// und die zwei Seiten für „gibt es nicht“ und „nicht deine Rolle“.

import type { ReactNode } from "react";
import { Chip, Ikon, Spaeter } from "@happy-energy/oberflaeche";
import { kisteFinden, STUFE_TEXT } from "./kisten.ts";
import { NichtDeineRolle } from "./laden.tsx";
import { startpfad } from "./navigation.ts";
import { Seite } from "./rahmen.tsx";
import { useCrm } from "./zusammenhang.tsx";

/** Eine Seite, deren Inhalt mit dem nächsten Stand kommt. */
export function PlatzhalterSeite({ titel, unter, was, wann = "nächster Stand", children }: { titel: string; unter?: string; was: string; wann?: string; children?: ReactNode }) {
  return (
    <Seite titel={titel} {...(unter ? { unter } : {})}>
      <Spaeter titel={titel} wann={wann}>
        {was}
      </Spaeter>
      {children}
    </Seite>
  );
}

/** Eine Kiste, die noch nicht (ganz) gebaut ist – was sie tut, was kommt, wann; bis dahin läuft es wie heute. */
export function KistenSeite({ schluessel }: { schluessel: string }) {
  const k = kisteFinden(schluessel);
  if (!k) return <UnbekannteSeite />;
  return (
    <Seite titel={k.name} unter={k.wer}>
      <section className="crm-karte kiste-karte">
        <div className="kiste-karte-kopf">
          <span className="avatar avatar-dunkel">
            <Ikon name={k.ikon} />
          </span>
          {k.stufe === "spaeter" ? (
            <Chip art="sp" ikon="uhr">
              {k.wann}
            </Chip>
          ) : (
            <Chip art="lime" ikon="haken">
              {STUFE_TEXT[k.stufe]}
            </Chip>
          )}
        </div>
        <p className="kiste-satz">{k.satz}</p>
        {k.schonDa && (
          <p className="kiste-schon-da">
            <Ikon name="haken" groesse="k" /> <b>{k.stufe === "spaeter" ? "Bis dahin:" : "Schon da:"}</b> {k.schonDa}
          </p>
        )}
        <p className="leise klein">Bis die Kiste gebaut ist, läuft das wie heute. {k.stufe !== "spaeter" && `Mehr kommt: ${k.wann}.`}</p>
      </section>
      {k.punkte.length > 0 && (
        // Was dort hinkommt – gestrichelt, mit dem Wann (Grundsatz 8).
        <section className="spaeter-block kiste-spaeter" aria-label="Was hier kommt">
          <div className="spaeter-kopf">
            <h4>Was hier kommt</h4>
            <Chip art="sp" ikon="uhr">
              {k.wann}
            </Chip>
          </div>
          <ul className="kiste-punkte">
            {k.punkte.map((p) => (
              <li key={p}>
                <Ikon name="uhr" groesse="k" /> {p}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Seite>
  );
}

/** Eine Adresse, die es nicht gibt (vertippt, alter Link). */
export function UnbekannteSeite() {
  const { ich } = useCrm();
  return (
    <Seite titel="Diese Seite gibt es nicht">
      <p className="leise">Vielleicht ein alter Link oder ein Tippfehler in der Adresse.</p>
      <div>
        <a className="crm-knopf rand" href={`#${startpfad(ich.nutzer.rolle)}`}>
          <Ikon name="haus" groesse="k" /> Zur Startseite
        </a>
      </div>
    </Seite>
  );
}

/** Eine Seite, die die Rolle nicht öffnen darf – freundlich, mit dem Weg zur eigenen Startseite. */
export function NichtDeineRolleSeite() {
  return (
    <Seite titel="Nicht für deine Rolle">
      <NichtDeineRolle />
    </Seite>
  );
}
